import React, { useState, useEffect } from 'react';
import { employeeAPI, documentAPI } from '../../services/api';
import { useGroup } from '../../contexts/GroupContext';
import { formatDateForInput, isValidEmail, isValidMobile, isValidDate } from '../../utils/helpers';
import { GENDER_OPTIONS, VALIDATION_MESSAGES, ERROR_MESSAGES, SUCCESS_MESSAGES } from '../../utils/constants';
import { DocumentIcon } from '../Common/Icons';

const EmployeeForm = ({ employee, onClose, onSubmit }) => {
  const { groups, selectedGroupId } = useGroup();
  const [formData, setFormData] = useState({
    employee_id: '',
    full_name: '',
    dob: '',
    gender: '',
    mobile: '',
    email: '',
    photo_path: '',
    department: '',
    designation: '',
    consent: false,
    photo_presigned_url: '',
    signature_path: '',
    blood_group: '',
    joining_date: '',
    retirement_date: '',
    identification_mark: '',
    id_cardno: '',
    group_id: selectedGroupId || '',
  });
  console.log("the form data is :: ", formData);
  

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState({ photo: false, signature: false });
  const [photoFile, setPhotoFile] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generateSuccess, setGenerateSuccess] = useState(null);

  // Paths are now stored correctly server-resolved; just pass through.
  const passthroughUrl = (p) => (p && String(p).trim()) || null;

  // Handle file selection for photo
  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // If employee exists, upload immediately
      if (employee?.employee_id) {
        // doUpload('photo', file);
      }
    }
  };

  // Handle file selection for signature
  const handleSignatureChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSignatureFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignaturePreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // If employee exists, upload immediately
      if (employee?.employee_id) {
        // doUpload('signature', file);
      }
    }
  };

  const doUpload = async (kind, file) => {
    if (!file) return;
    
    // For new employees, we'll upload after creation
    if (!employee?.employee_id) {
      return;
    }
    
    try {
      setUploading(prev => ({ ...prev, [kind]: true }));
      const apiFn = kind === 'photo' ? employeeAPI.uploadPhoto : employeeAPI.uploadSignature;
      const resp = await apiFn(employee.employee_id, file);
      const newPath = resp?.data?.data?.photo_path || resp?.data?.data?.signature_path;
      if (newPath) {
        setFormData(prev => ({ ...prev, [`${kind}_path`]: newPath }));
        // Clear file state after successful upload
        if (kind === 'photo') {
          setPhotoFile(null);
        } else {
          setSignatureFile(null);
        }
      }
    } catch (e) {
      console.error('Upload failed:', e);
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(prev => ({ ...prev, [kind]: false }));
    }
  };

  useEffect(() => {
    const loadEmployeeData = async () => {
    if (employee) {
        // If employee doesn't have presigned URLs, fetch fresh data
        let employeeData = employee;
        if (!employee.photo_presigned_url && !employee.signature_presigned_url && employee.employee_id) {
          try {
            const response = await employeeAPI.getById(employee.employee_id);
            employeeData = response.data.data;
          } catch (err) {
            console.warn('Failed to fetch employee details, using provided data:', err);
          }
        }

      setFormData({
          employee_id: employeeData.employee_id || '',
          full_name: employeeData.full_name || '',
          dob: formatDateForInput(employeeData.dob) || '',
          gender: employeeData.gender || '',
          mobile: employeeData.mobile || '',
          email: employeeData.email || '',
          photo_path: employeeData.photo_presigned_url || employeeData.photo_path || '',
          department: employeeData.department || '',
          designation: employeeData.designation || '',
          consent: employeeData.consent || false,
          signature_path: employeeData.signature_presigned_url || employeeData.signature_path || '',
          blood_group: employeeData.blood_group || '',
          joining_date: formatDateForInput(employeeData.joining_date) || '',
          retirement_date: formatDateForInput(employeeData.retirement_date) || '',
          identification_mark: employeeData.identification_mark || '',
          id_cardno: employeeData.id_cardno || '',
          group_id: employeeData.group_id || selectedGroupId || '',
      });
        
        // Set previews from presigned URLs (generated by backend)
        // The backend generates photo_presigned_url and signature_presigned_url when fetching employees
        if (employeeData.photo_presigned_url) {
          setPhotoPreview(employeeData.photo_presigned_url);
        } else {
          setPhotoPreview(null);
        }
        
        if (employeeData.signature_presigned_url) {
          setSignaturePreview(employeeData.signature_presigned_url);
        } else {
          setSignaturePreview(null);
        }
    } else {
        // Reset file states when creating new employee
        setPhotoFile(null);
        setSignatureFile(null);
        setPhotoPreview(null);
        setSignaturePreview(null);
      // For new employee, set default group
      setFormData(prev => ({
        ...prev,
        group_id: selectedGroupId || prev.group_id || '',
      }));
    }
    };

    loadEmployeeData();
  }, [employee, selectedGroupId]);

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.employee_id.trim()) {
      newErrors.employee_id = VALIDATION_MESSAGES.REQUIRED;
    }

    if (!formData.full_name.trim()) {
      newErrors.full_name = VALIDATION_MESSAGES.REQUIRED;
    }

    if (!formData.group_id) {
      newErrors.group_id = 'Group is required';
    }

    if (!formData.dob) {
      newErrors.dob = VALIDATION_MESSAGES.REQUIRED;
    } else if (!isValidDate(formData.dob)) {
      newErrors.dob = VALIDATION_MESSAGES.INVALID_DATE;
    }

    if (formData.email && !isValidEmail(formData.email)) {
      newErrors.email = VALIDATION_MESSAGES.INVALID_EMAIL;
    }

    if (formData.mobile && !isValidMobile(formData.mobile)) {
      newErrors.mobile = VALIDATION_MESSAGES.INVALID_MOBILE;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      // Clean form data - remove empty strings and unnecessary fields
      const cleanData = { ...formData };
      
      // Remove photo_presigned_url as it's not a database field
      delete cleanData.photo_presigned_url;
      
      // Handle date fields - convert empty strings to null
      const dateFields = ['joining_date', 'retirement_date'];
      dateFields.forEach(field => {
        if (cleanData[field] === '' || !cleanData[field]) {
          cleanData[field] = null;
        }
      });
      
      // Convert empty strings to null for optional string fields
      const optionalStringFields = ['photo_path', 'signature_path', 'mobile', 'email', 'gender', 
                                   'department', 'designation', 'blood_group', 
                                   'identification_mark', 'id_cardno'];
      
      optionalStringFields.forEach(field => {
        if (cleanData[field] === '') {
          cleanData[field] = null;
        }
      });
      
      // For new employees or when updating with files, don't send photo_path/signature_path if files will be uploaded
      if (photoFile) {
        delete cleanData.photo_path; // Will be set by backend after file upload
      }
      if (signatureFile) {
        delete cleanData.signature_path; // Will be set by backend after file upload
      }

      let savedEmployee;
      if (employee) {
        // Update existing employee with files if available
        const response = await employeeAPI.update(employee.employee_id, cleanData, photoFile, signatureFile);
        savedEmployee = response.data.data;
      } else {
        // Create new employee with files if available
        const response = await employeeAPI.create(cleanData, photoFile, signatureFile);
        savedEmployee = response.data.data;
      }

      onSubmit();
    } catch (err) {
      console.error('Error saving employee:', err);
      console.error('Error details:', err.response?.data);
      if (err.response?.data?.error?.includes('already exists')) {
        setErrors({ employee_id: VALIDATION_MESSAGES.DUPLICATE_EMPLOYEE_ID });
      } else {
        const errorMessage = err.response?.data?.error || ERROR_MESSAGES.NETWORK_ERROR;
        setErrors({ general: errorMessage });
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle document generation
  const handleGenerateDocument = async () => {
    if (!employee?.employee_id) {
      setErrors({ general: 'Employee must be saved before generating document' });
      return;
    }

    try {
      setGenerating(true);
      setErrors({});
      setGenerateSuccess(null);

      const payload = {
        employee_id: employee.employee_id,
        doc_type: 'IDCER',
        issuer_id: 'in.gov.maharashtrasrpf',
        issue_date: new Date().toISOString().slice(0, 10),
      };

      const response = await documentAPI.generateFromEmployee(payload);
      const document = response.data?.data;
      
      if (document) {
        setGenerateSuccess(`Document generated successfully! Document ID: ${document.doc_id}`);
        // Clear success message after 5 seconds
        setTimeout(() => setGenerateSuccess(null), 5000);
      } else {
        setErrors({ general: 'Document generation completed but no document data returned' });
      }
    } catch (err) {
      console.error('Error generating document:', err);
      const errorMessage = err.response?.data?.error || ERROR_MESSAGES.NETWORK_ERROR;
      setErrors({ general: errorMessage });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>{employee ? 'Edit Employee' : 'Add New Employee'}</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="employee-form">
          {errors.general && (
            <div className="alert alert-danger">{errors.general}</div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="employee_id">Employee ID *</label>
              <input
                type="text"
                id="employee_id"
                name="employee_id"
                value={formData.employee_id}
                onChange={handleChange}
                className={`form-control ${errors.employee_id ? 'is-invalid' : ''}`}
                placeholder="e.g., EMP2025-001"
                disabled={!!employee}
              />
              {errors.employee_id && <div className="invalid-feedback">{errors.employee_id}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="full_name">Full Name *</label>
              <input
                type="text"
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                className={`form-control ${errors.full_name ? 'is-invalid' : ''}`}
                placeholder="Enter full name"
              />
              {errors.full_name && <div className="invalid-feedback">{errors.full_name}</div>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="id_cardno">ID Card Number</label>
              <input
                type="text"
                id="id_cardno"
                name="id_cardno"
                value={formData.id_cardno}
                onChange={handleChange}
                className="form-control"
                placeholder="Enter ID card number"
              />
            </div>
            {!employee && (
              <div className="form-group">
                <label htmlFor="group_id">Group *</label>
                <select
                  id="group_id"
                  name="group_id"
                  value={formData.group_id}
                  onChange={handleChange}
                  className={`form-control ${errors.group_id ? 'is-invalid' : ''}`}
                  required
                >
                  <option value="">Select Group</option>
                  {groups.map(group => (
                    <option key={group.id} value={group.id}>
                      {group.name} {group.customer ? `(${group.customer.name})` : ''}
                    </option>
                  ))}
                </select>
                {errors.group_id && <div className="invalid-feedback">{errors.group_id}</div>}
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="dob">Date of Birth *</label>
              <input
                type="date"
                id="dob"
                name="dob"
                value={formData.dob}
                onChange={handleChange}
                className={`form-control ${errors.dob ? 'is-invalid' : ''}`}
              />
              {errors.dob && <div className="invalid-feedback">{errors.dob}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="gender">Gender</label>
              <select
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className="form-control"
              >
                <option value="">Select Gender</option>
                {GENDER_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="mobile">Mobile</label>
              <input
                type="tel"
                id="mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                className={`form-control ${errors.mobile ? 'is-invalid' : ''}`}
                placeholder="9876543210"
              />
              {errors.mobile && <div className="invalid-feedback">{errors.mobile}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                placeholder="email@example.com"
              />
              {errors.email && <div className="invalid-feedback">{errors.email}</div>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="department">Department</label>
              <input
                type="text"
                id="department"
                name="department"
                value={formData.department}
                onChange={handleChange}
                className="form-control"
                placeholder="Enter department"
              />
            </div>

            <div className="form-group">
              <label htmlFor="designation">Designation</label>
              <input
                type="text"
                id="designation"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                className="form-control"
                placeholder="Enter designation"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="photo_upload">Employee Photo</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {photoPreview && (
                  <div style={{ marginBottom: '8px' }}>
                    <img
                      src={photoPreview}
                      alt="Photo preview"
                      style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  </div>
                )}
                <label className="btn btn-secondary" style={{ margin: 0, cursor: 'pointer', display: 'inline-block', width: 'fit-content' }}>
                  {uploading.photo ? 'Uploading...' : (photoPreview ? 'Change Photo' : 'Upload Photo')}
              <input
                    type="file"
                    id="photo_upload"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handlePhotoChange}
                    disabled={uploading.photo}
                  />
                </label>
                {photoFile && !employee && (
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    {photoFile.name} (will be uploaded after saving)
                  </span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="signature_upload">Employee Signature</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {signaturePreview && (
                  <div style={{ marginBottom: '8px' }}>
                    <img
                      src={signaturePreview}
                      alt="Signature preview"
                      style={{ width: 200, height: 70, objectFit: 'contain', background: '#fff', borderRadius: 2, border: '1px solid #ddd' }}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
              )}
                <label className="btn btn-secondary" style={{ margin: 0, cursor: 'pointer', display: 'inline-block', width: 'fit-content' }}>
                  {uploading.signature ? 'Uploading...' : (signaturePreview ? 'Change Signature' : 'Upload Signature')}
                  <input
                    type="file"
                    id="signature_upload"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleSignatureChange}
                    disabled={uploading.signature}
                  />
                </label>
                {signatureFile && !employee && (
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    {signatureFile.name} (will be uploaded after saving)
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="blood_group">Blood Group</label>
              <input
                type="text"
                id="blood_group"
                name="blood_group"
                value={formData.blood_group}
                onChange={handleChange}
                className="form-control"
                placeholder="e.g., A+, B+, O+, AB+"
              />
            </div>

            <div className="form-group">
              <label htmlFor="id_cardno">ID Card Number</label>
              <input
                type="text"
                id="id_cardno"
                name="id_cardno"
                value={formData.id_cardno}
                onChange={handleChange}
                className="form-control"
                placeholder="Enter ID card number"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="joining_date">Date of Joining</label>
              <input
                type="date"
                id="joining_date"
                name="joining_date"
                value={formData.joining_date}
                onChange={handleChange}
                className="form-control"
              />
                </div>

            <div className="form-group">
              <label htmlFor="retirement_date">Date of Retirement</label>
              <input
                type="date"
                id="retirement_date"
                name="retirement_date"
                value={formData.retirement_date}
                onChange={handleChange}
                className="form-control"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="identification_mark">Identification Mark</label>
            <input
              type="text"
              id="identification_mark"
              name="identification_mark"
              value={formData.identification_mark}
              onChange={handleChange}
              className="form-control"
              placeholder="e.g., Mole on left hand, Scar on forehead"
            />
          </div>

          <div className="form-group">
            <div className="form-check">
              <input
                type="checkbox"
                id="consent"
                name="consent"
                checked={formData.consent}
                onChange={handleChange}
                className="form-check-input"
              />
              <label htmlFor="consent" className="form-check-label">
                Consent for DigiLocker
              </label>
            </div>
          </div>

          {generateSuccess && (
            <div style={{ 
              padding: '0.75rem', 
              marginBottom: '1rem', 
              background: '#d1fae5', 
              border: '1px solid #10b981', 
              borderRadius: '0.5rem',
              color: '#059669',
              fontSize: '0.875rem'
            }}>
              ✓ {generateSuccess}
            </div>
          )}

          <div className="form-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
              {employee && (
                <button 
                  type="button" 
                  className="btn btn-outline" 
                  onClick={handleGenerateDocument}
                  disabled={generating || loading}
                  style={{
                    background: generating ? '#f3f4f6' : 'white',
                    borderColor: '#3b82f6',
                    color: '#3b82f6'
                  }}
                >
                  {generating ? 'Generating...' : (
                    <>
                     <i class="fa-solid fa-file-lines"></i> Generate Document
                    </>
                  )}
                </button>
              )}
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading || generating}>
              {loading ? 'Saving...' : (employee ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeeForm;
